import { useEffect, useRef, useState } from 'react'

import { getOrganizationClient } from '@/api'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/use-toast'
import { IOrganization } from '@/models'

const POLL_INTERVAL_MS = 5000

type ResetStep =
  | 'SNAPSHOT'
  | 'WIPE_ENTITIES'
  | 'DELETE_CONFIGS'
  | 'DELETE_BLUEPRINT_RECORDS'
  | 'REINSTALL_BASE_MIGRATION'
  | 'DONE'

export interface SandboxFactoryReset {
  org_id: string
  snapshot_id?: string
  status: 'running' | 'succeeded' | 'failed'
  step: ResetStep
  failed_step?: ResetStep
  error?: string
}

const STEP_LABEL: Record<ResetStep, string> = {
  SNAPSHOT: 'Taking a snapshot of the organization',
  WIPE_ENTITIES: 'Wiping all entities',
  DELETE_CONFIGS: 'Deleting configuration resources',
  DELETE_BLUEPRINT_RECORDS: 'Deleting blueprint records',
  REINSTALL_BASE_MIGRATION: 'Reinstalling the base-360 migration',
  DONE: 'Done'
}

/** The backend enforces the same rule; this only decides whether the button is offered. */
export const isSandboxOrganization = (organization?: IOrganization) =>
  organization?.organization_use === 'Sandbox'

// TODO: switch to the typed operations once @epilot/organization-client-internal ships them
const base = (orgId: string) =>
  `/v2/internal/organizations/${orgId}/sandbox-factory-reset`
const startReset = (orgId: string, confirmation: string) =>
  getOrganizationClient().post<SandboxFactoryReset>(base(orgId), {
    confirmation
  })
const advanceReset = (orgId: string) =>
  getOrganizationClient().post<SandboxFactoryReset>(`${base(orgId)}:advance`)

const getErrorMessage = (err: unknown) => {
  const error = err as {
    response?: { data?: { error?: string; message?: string } }
    message?: string
  }

  return (
    error?.response?.data?.error ??
    error?.response?.data?.message ??
    error?.message ??
    'An unexpected error occurred.'
  )
}

interface SandboxFactoryResetDialogProps {
  organization?: IOrganization
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SandboxFactoryResetDialog({
  organization,
  open,
  onOpenChange
}: SandboxFactoryResetDialogProps) {
  const [input, setInput] = useState('')
  const [isStarting, setIsStarting] = useState(false)
  const [run, setRun] = useState<SandboxFactoryReset>()
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const orgIdRef = useRef<string>()
  const startInFlight = useRef(false)

  const orgId = organization?.id ? String(organization.id) : undefined
  const expectedName = organization?.name || orgId || ''
  const isRunning = run?.status === 'running'

  orgIdRef.current = orgId

  // The backend performs one step per call, so keep calling it while the reset runs
  useEffect(() => {
    if (!orgId || !isRunning) return

    timer.current = setTimeout(async () => {
      try {
        const nextRun = (await advanceReset(orgId)).data

        // the dialog may have been closed or pointed at another organization meanwhile
        if (orgIdRef.current !== orgId) return
        setRun(nextRun)
      } catch (err) {
        // transient errors must not stop the reset, the next poll continues it
        toast({
          title: 'Could not check the factory reset progress',
          description: getErrorMessage(err)
        })
        setRun((current) => (current ? { ...current } : current))
      }
    }, POLL_INTERVAL_MS)

    return () => clearTimeout(timer.current)
  }, [orgId, isRunning, run])

  // ignore responses that arrive after unmount
  useEffect(
    () => () => {
      orgIdRef.current = undefined
    },
    []
  )

  useEffect(() => {
    if (open && !isRunning) setInput('')
  }, [open, isRunning])

  const handleStart = async () => {
    // state updates are async, so guard double clicks with a ref
    if (!orgId || startInFlight.current) return
    startInFlight.current = true
    setIsStarting(true)

    try {
      setRun((await startReset(orgId, input)).data)
    } catch (err) {
      toast({
        title: 'Failed to start the factory reset',
        description: getErrorMessage(err)
      })
    } finally {
      startInFlight.current = false
      setIsStarting(false)
    }
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-primary">
            {`Factory reset "${organization?.name ?? ''}"`}
          </DialogTitle>
          <DialogDescription>
            This is irreversible. It first takes a snapshot of the organization,
            then wipes all entities, deletes all configuration resources and
            blueprint records, then reinstalls the base-360 migration from
            scratch. Users with their role assignments and snapshots are kept;
            default role grants are reset. Pause the org&apos;s inbound
            integrations first. It can take a long time; keep this dialog open
            to follow the progress.
          </DialogDescription>
        </DialogHeader>

        {run ? (
          <div className="space-y-2 text-sm">
            <p>
              {run.status === 'running' &&
                `In progress: ${STEP_LABEL[run.step]}…`}
              {run.status === 'succeeded' &&
                `Factory reset finished${
                  run.snapshot_id
                    ? ` (snapshot ${run.snapshot_id} was taken first)`
                    : ''
                }. The base-360 migration is installing in the background.`}
              {run.status === 'failed' &&
                `Failed during "${STEP_LABEL[run.failed_step ?? run.step]}": ${
                  run.error ?? 'unknown error'
                }`}
            </p>
          </div>
        ) : (
          <div>
            <Label htmlFor="factory-reset-confirmation">
              {`Please type "${expectedName}" to proceed`}
            </Label>
            <Input
              id="factory-reset-confirmation"
              onChange={(e) => setInput(e.target.value)}
              placeholder={expectedName}
              value={input}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            disabled={isStarting}
            onClick={() => onOpenChange(false)}
            variant="outline"
          >
            {run ? 'Close' : 'Cancel'}
          </Button>
          {(!run || run.status === 'failed') && (
            <Button
              disabled={isStarting || input !== expectedName}
              onClick={handleStart}
              variant="destructive"
            >
              {isStarting
                ? 'Starting…'
                : run
                ? 'Restart factory reset'
                : 'Factory reset'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
